/**
 * Rule conditions on what an AI event reported — the client half of the command-center's
 * services/ruleConditions.js. Keep the operator list and validation in step with it.
 *
 * Stored on alert_configs as:
 *   conditions       { match: 'all'|'any', rules: [{ field, op, value }] } | null
 *   sustain_seconds  the conditions must hold this long before the rule fires (0 = at once)
 *   message_template the alert text, {field} placeholders filled from the event
 */

export type ConditionOp =
  | 'lt' | 'lte' | 'gt' | 'gte' | 'eq' | 'neq'
  | 'between' | 'outside' | 'in' | 'not_in' | 'contains' | 'not_contains' | 'starts_with' | 'matches'
  | 'exists' | 'not_exists';

export interface RuleCondition {
  field: string;
  op: ConditionOp;
  value?: unknown;
}

export interface RuleConditions {
  match: 'all' | 'any';
  rules: RuleCondition[];
}

export interface CatalogField {
  type: 'number' | 'string' | 'boolean';
  min?: number;
  max?: number;
  samples?: string[];
}

/** One row of the command-center's ai_event_catalog (GET /config/event-catalog). */
export interface EventCatalogEntry {
  event_type: string;
  event_subtype: string;
  fields: Record<string, CatalogField>;
  event_count?: number | string;
  last_seen_at?: string;
}

/** How an operator's value input behaves for each operator. */
export type OpKind = 'number' | 'text' | 'range' | 'list' | 'none';

export const OP_OPTIONS: { value: ConditionOp; label: string; kind: OpKind }[] = [
  { value: 'lt', label: '<', kind: 'number' },
  { value: 'lte', label: '≤', kind: 'number' },
  { value: 'gt', label: '>', kind: 'number' },
  { value: 'gte', label: '≥', kind: 'number' },
  { value: 'eq', label: '=', kind: 'text' },
  { value: 'neq', label: '≠', kind: 'text' },
  { value: 'between', label: 'between', kind: 'range' },
  { value: 'outside', label: 'outside', kind: 'range' },
  { value: 'in', label: 'is one of', kind: 'list' },
  { value: 'not_in', label: 'is not one of', kind: 'list' },
  { value: 'contains', label: 'contains', kind: 'text' },
  { value: 'not_contains', label: 'does not contain', kind: 'text' },
  { value: 'starts_with', label: 'starts with', kind: 'text' },
  { value: 'matches', label: 'matches pattern', kind: 'text' },
  { value: 'exists', label: 'is reported', kind: 'none' },
  { value: 'not_exists', label: 'is missing', kind: 'none' },
];

/**
 * The tests in words, by what the field holds — what the rule form offers. A field of unknown
 * type (typed by hand) gets both lists.
 */
export const OP_WORDS: Record<'number' | 'text', { value: ConditionOp; label: string }[]> = {
  number: [
    { value: 'gt', label: 'is greater than' },
    { value: 'lt', label: 'is less than' },
    { value: 'gte', label: 'is at least' },
    { value: 'lte', label: 'is at most' },
    { value: 'between', label: 'is between' },
    { value: 'outside', label: 'is outside' },
  ],
  text: [
    { value: 'eq', label: 'is equal to' },
    { value: 'neq', label: 'is not equal to' },
    { value: 'contains', label: 'contains' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'starts_with', label: 'starts with' },
    { value: 'matches', label: 'matches the pattern' },
    { value: 'in', label: 'is one of' },
    { value: 'not_in', label: 'is none of' },
  ],
};
export const OP_WORD: Record<string, string> = Object.fromEntries(
  [...OP_WORDS.number, ...OP_WORDS.text, { value: 'exists', label: 'is reported' }, { value: 'not_exists', label: 'is missing' }]
    .map((o) => [o.value, o.label]),
);

export const opKind = (op: string): OpKind => OP_OPTIONS.find((o) => o.value === op)?.kind ?? 'text';
const opLabel = (op: string) => OP_OPTIONS.find((o) => o.value === op)?.label ?? op;

/** Fields every event has, offered alongside the catalog's. */
export const BUILTIN_FIELDS: Record<string, CatalogField> = {
  severity: { type: 'string', samples: ['critical', 'warning', 'normal'] },
  event_subtype: { type: 'string' },
  // Everything an OCR event read (plates + text lines) — filled in by the command-center.
  read_text: { type: 'string' },
};

/** How a field reads in the rule form. */
export const FIELD_LABEL: Record<string, string> = {
  read_text: 'Text the AI read (OCR)',
  severity: 'Severity the AI gave',
  event_subtype: 'Kind of event',
  sustained_s: 'How long it has lasted (s)',
};

/** A rule's conditions, whatever shape they arrived in; null when it has none. */
export function parseConditions(raw: unknown): RuleConditions | null {
  let c: any = raw;
  if (typeof c === 'string') {
    try { c = JSON.parse(c); } catch { return null; }
  }
  if (!c || !Array.isArray(c.rules)) return null;
  return { match: c.match === 'any' ? 'any' : 'all', rules: c.rules };
}

const titleCase = (s: string) =>
  s.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().replace(/\b\w/g, (m) => m.toUpperCase());

/**
 * Event-type options for the rule's type picker, from what the AI module has actually sent.
 *
 * Two levels, labelled so the difference reads at a glance:
 *   "Belt Movement — all events"          the model: matches every event it sends
 *   "Belt Movement › No Belt Movement"    one kind of event from that model, and nothing else
 * Each model is followed by its own kinds, so the list reads as a tree.
 */
export function catalogEventOptions(catalog: EventCatalogEntry[]): { value: string; label: string }[] {
  const kinds = new Map<string, Set<string>>();
  for (const e of catalog || []) {
    if (!e.event_type) continue;
    if (!kinds.has(e.event_type)) kinds.set(e.event_type, new Set());
    if (e.event_subtype && e.event_subtype !== e.event_type) kinds.get(e.event_type)!.add(e.event_subtype);
  }
  const out: { value: string; label: string }[] = [];
  const used = new Set<string>();
  for (const [type, subs] of [...kinds].sort(([a], [b]) => a.localeCompare(b))) {
    if (!used.has(type)) {
      out.push({ value: type, label: `${titleCase(type)} — all events` });
      used.add(type);
    }
    for (const sub of [...subs].sort()) {
      if (used.has(sub)) continue;
      out.push({ value: sub, label: `${titleCase(type)} › ${titleCase(sub)}` });
      used.add(sub);
    }
  }
  return out;
}

/**
 * The metadata fields a rule targeting `eventTypes` can test: the union of the catalog
 * entries it matches (by type or sub-type; '*' or nothing selected = every entry).
 */
export function fieldsFor(catalog: EventCatalogEntry[], eventTypes: string[]): Record<string, CatalogField> {
  const all = eventTypes.length === 0 || eventTypes.includes('*');
  const out: Record<string, CatalogField> = {};
  for (const e of catalog || []) {
    if (!all && !eventTypes.includes(e.event_type) && !eventTypes.includes(e.event_subtype)) continue;
    for (const [name, f] of Object.entries(e.fields || {})) {
      const prev = out[name];
      out[name] = prev
        ? {
          type: prev.type === f.type ? prev.type : 'string',
          min: Math.min(prev.min ?? Infinity, f.min ?? Infinity),
          max: Math.max(prev.max ?? -Infinity, f.max ?? -Infinity),
          samples: [...new Set([...(prev.samples || []), ...(f.samples || [])])].slice(0, 10),
        }
        : f;
    }
  }
  return out;
}

const isNum = (v: unknown) => v !== '' && v !== null && v !== undefined && Number.isFinite(Number(v));

/** What can't be saved, in the operator's terms. '' when fine. Mirrors the server's check. */
const OCR_NAMES = ['ocr', 'ocr_detection', 'text_detection'];
/** Same check as the command-center: a backreference, or a repeated group that itself repeats. */
const NESTED_Q = /\((?:[^()\\]|\\.)*(?:[+*]|\{\d+,\d*\})(?:[^()\\]|\\.)*\)(?:[+*]|\{\d+,\d*\})/;
export const unsafePattern = (p: unknown) => /\\[1-9]/.test(String(p)) || NESTED_Q.test(String(p));

export function conditionsProblem(form: {
  conditions?: unknown; sustain_seconds?: unknown; message_template?: unknown; event_type?: unknown;
}): string {
  const s = form.sustain_seconds;
  if (s !== undefined && s !== null && s !== '') {
    const n = Number(s);
    if (!Number.isInteger(n) || n < 0 || n > 86400) return 'Hold time must be whole seconds, 0 – 86400.';
  }
  if (typeof form.message_template === 'string' && form.message_template.length > 500) {
    return 'Alert message is limited to 500 characters.';
  }
  const c = parseConditions(form.conditions);
  // An OCR camera reads every frame: a rule with nothing to test in what was read never alerts.
  const names = String(form.event_type || '').split(',').map((n) => n.trim().toLowerCase().replace(/[\s.-]+/g, '_'));
  if (names.some((n) => OCR_NAMES.includes(n)) && !(c && c.rules.length)) {
    return 'An OCR rule needs a condition on what was read — e.g. read_text contains a word.';
  }
  if (!c) return '';
  for (const [i, r] of c.rules.entries()) {
    const at = `Condition ${i + 1}`;
    if (!String(r.field || '').trim()) return `${at}: pick the field to test.`;
    const kind = opKind(r.op);
    if (kind === 'none') continue;
    if (kind === 'range') {
      const v = r.value as unknown[];
      if (!Array.isArray(v) || v.length !== 2 || !v.every(isNum)) return `${at}: enter a low and a high number.`;
      continue;
    }
    if (kind === 'list') {
      if (!Array.isArray(r.value) || r.value.length === 0) return `${at}: enter at least one value.`;
      continue;
    }
    if (r.value === undefined || r.value === null || r.value === '') return `${at}: enter a value.`;
    if (kind === 'number' && !isNum(r.value)) return `${at}: '${OP_WORD[r.op] || opLabel(r.op)}' needs a number.`;
    if (r.op === 'matches') {
      if (String(r.value).length > 200) return `${at}: a pattern can be at most 200 characters.`;
      if (unsafePattern(r.value)) return `${at}: that pattern repeats a repeated part (like (A+)+) or refers back (\\1), which can freeze alerting — simplify it, e.g. ^[A-Z0-9 ]+$.`;
      try { new RegExp(String(r.value), 'i'); } catch { return `${at}: that pattern is not valid — e.g. ^GJ\\d{2} or (PAL|PLB).`; }
    }
  }
  return '';
}

function valueText(r: RuleCondition): string {
  const kind = opKind(r.op);
  if (kind === 'none') return '';
  if (kind === 'range' && Array.isArray(r.value)) return `${r.value[0]}–${r.value[1]}`;
  if (kind === 'list' && Array.isArray(r.value)) return r.value.join(', ');
  return String(r.value ?? '');
}

/** One-line reading of a rule's conditions for the rules table, or '' when it has none. */
export function conditionsSummary(cfg: { conditions?: unknown; sustain_seconds?: unknown }): string {
  const c = parseConditions(cfg.conditions);
  const parts = (c?.rules || []).map((r) => `${FIELD_LABEL[r.field] || r.field} ${OP_WORD[r.op] || opLabel(r.op)} ${valueText(r)}`.trim());
  let text = parts.join(c?.match === 'any' ? ' or ' : ' and ');
  const hold = Number(cfg.sustain_seconds) || 0;
  if (hold > 0) text = `${text || 'it is seen'} for ${formatSeconds(hold)}`;
  return text;
}

export function formatSeconds(s: number): string {
  if (s < 60) return `${s}s`;
  if (s < 3600) return s % 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s / 60}m`;
  const m = Math.round((s % 3600) / 60);
  return m ? `${Math.floor(s / 3600)}h ${m}m` : `${s / 3600}h`;
}

/** Turn what the operator typed into the stored value for the operator chosen. */
export function coerceValue(op: string, raw: string | [string, string]): unknown {
  const kind = opKind(op);
  if (kind === 'none') return undefined;
  if (kind === 'range') {
    const [a, b] = Array.isArray(raw) ? raw : ['', ''];
    return [isNum(a) ? Number(a) : a, isNum(b) ? Number(b) : b];
  }
  if (kind === 'list') {
    return String(raw).split(',').map((v) => v.trim()).filter(Boolean);
  }
  if (kind === 'number') return isNum(raw) ? Number(raw) : raw;
  return raw;
}
